/**
 * How Home's lists and tooltips write dates and spans: "3 Nov" in a column,
 * "30 October" in a tooltip, "3 hours ago" in words. The year shows only
 * outside the current one.
 *
 * @module components/home/homeDates.logic
 */
import { daysUntil } from "./HomeAgenda.logic";

const yearUnlessCurrent = (at: number, now: number) =>
  new Date(at).getFullYear() === new Date(now).getFullYear() ? {} : { year: "numeric" as const };

/** A date column: "3 Nov", or "3 Jan 2027" outside the current year. */
export const dayMonth = (at: number, now: number) =>
  new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "short", ...yearUnlessCurrent(at, now) });

/** A tooltip's date: "30 October", with the year outside the current one. */
export const longDayMonth = (at: number, now: number) =>
  new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "long", ...yearUnlessCurrent(at, now) });

/** "Thursday 22 October 2026", without the comma en-GB puts after the weekday. */
export const fullDate = (at: number) =>
  `${new Date(at).toLocaleDateString("en-GB", { weekday: "long" })} ${new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`;

/** A day in the past: "Today", "Yesterday" or "3 days ago" within a week, then its date. */
export function pastDay(at: number, now: number): string {
  const days = -daysUntil(at, now);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return days < 7 ? `${days} days ago` : dayMonth(at, now);
}

/** How long ago in words: "just now", "3 hours ago", "yesterday", "2 weeks ago". */
export function agoInWords(iso: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"} ago`;
  if (minutes < 2) return "just now";
  if (minutes < 60) return unit(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return unit(hours, "hour");
  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 14) return unit(days, "day");
  if (days < 60) return unit(Math.round(days / 7), "week");
  return unit(Math.round(days / 30), "month");
}

/** A scheduled run: "Mon 10:00" within a week, else "12 Nov 10:00". */
export function nextRunLabel(at: number, now: number): string {
  const time = new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const day = at - now < 7 * 86_400_000 ? new Date(at).toLocaleDateString("en-GB", { weekday: "short" }) : dayMonth(at, now);
  return `${day} ${time}`;
}

/** Every amount with pence, so £6.32 and £90.00 read as one format. */
export const pounds = (value: number) => `£${value.toFixed(2)}`;

export const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
